/**
 * 鼠标 SDK 公共 API (实例化层)
 * 类似于键盘的 commonApi.ts
 */

import { MouseService } from "./mouseService";
import { readMouseEquipmentDataFromStorage } from "./mouseSessionRead";
import {
  dispatchMouseBoardProfileChangedOnWindow,
  setMouseDeviceService,
} from "@eweadn-qiankun/shared-utils";
import {
  getFreqDpiGearCount,
  hasFixedFreqDpiGearCount,
} from "./freq/freqDefaultDpiPreset";
import {
  mouseGetStateCache,
  shouldKeepLastKeysAfterWrite,
} from "./getStateCache";

// 实例化鼠标服务
export const mouseService = new MouseService();
setMouseDeviceService(mouseService);

// 如果未来有具体的 SDK 实例，也可以在这里导出
// export const serviceMouseJiangmeng = ...

export const apiSuccessCode = (message: string) => {
  return {
    code: 200,
    message,
  };
};

export const apiErrorCode = (message: string) => {
  return {
    code: 500,
    message,
  };
};

/** DPI 档位颜色归一化为 { r, g, b } 0–255，供 getState / 匠盟合并使用 */
export type DpiColor = { r: number; g: number; b: number };

export function normalizeDpiColor(c: unknown): DpiColor {
  if (c == null) return { r: 255, g: 255, b: 255 };
  const x = c as Record<string, unknown>;
  const r = x.r ?? x.red ?? x.R;
  const g = x.g ?? x.green ?? x.G;
  const b = x.b ?? x.blue ?? x.B;

  if (typeof r === "number" && typeof g === "number" && typeof b === "number") {
    const scale = Math.max(r, g, b) <= 1 ? 255 : 1;
    return {
      r: Math.round(Math.max(0, Math.min(255, r * scale))),
      g: Math.round(Math.max(0, Math.min(255, g * scale))),
      b: Math.round(Math.max(0, Math.min(255, b * scale))),
    };
  }

  if (typeof c === "string") {
    const s = c.trim();
    const hexMatch = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(s);
    if (hexMatch) {
      return {
        r: parseInt(hexMatch[1], 16),
        g: parseInt(hexMatch[2], 16),
        b: parseInt(hexMatch[3], 16),
      };
    }
    const rgbMatch = s.match(/rgb\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/i);
    if (rgbMatch) {
      return {
        r: parseInt(rgbMatch[1], 10),
        g: parseInt(rgbMatch[2], 10),
        b: parseInt(rgbMatch[3], 10),
      };
    }
  }

  if (typeof c === "number") {
    return { r: (c >> 16) & 0xff, g: (c >> 8) & 0xff, b: c & 0xff };
  }

  return { r: 255, g: 255, b: 255 };
}

type RecordLike = Record<string, unknown>;

const stripSideCurrentRoute = (side: unknown): unknown => {
  if (!side || typeof side !== "object" || Array.isArray(side)) return side;
  const next = { ...(side as RecordLike) };
  delete next.currentRoute;
  return next;
};

/** getState / 整包 sync 不包含 currentRoute（由 StatusChanged 专用通道更新） */
function stripMouseCfgTriggerLiveRoutes(
  mouseCfg: RecordLike | undefined
): RecordLike | undefined {
  if (!mouseCfg || typeof mouseCfg !== "object") return mouseCfg;
  const next: RecordLike = { ...mouseCfg };
  const trigger = mouseCfg.trigger;
  if (trigger && typeof trigger === "object" && !Array.isArray(trigger)) {
    const t = trigger as RecordLike;
    next.trigger = {
      ...t,
      left: stripSideCurrentRoute(t.left),
      right: stripSideCurrentRoute(t.right),
    };
  }
  const cal = mouseCfg.magneticMicroswitchCal;
  if (cal && typeof cal === "object" && !Array.isArray(cal)) {
    const c = cal as RecordLike;
    next.magneticMicroswitchCal = {
      ...c,
      left: stripSideCurrentRoute(c.left),
      right: stripSideCurrentRoute(c.right),
    };
  }
  return next;
}

export function stripMouseDeviceInfoLiveRoutes(
  deviceInfo: RecordLike
): RecordLike {
  const mc = deviceInfo.mouseCfg;
  if (!mc || typeof mc !== "object") return deviceInfo;
  return {
    ...deviceInfo,
    mouseCfg: stripMouseCfgTriggerLiveRoutes(mc as RecordLike) ?? mc,
  };
}

type CurrentEquipmentData = {
  displayname?: string;
  productId: number;
  productName?: string;
  solutionRovider: string;
  vendorId: number;
} | null;

/**
 * 获取当前设备信息（从 localStorage.equipmentData 解析）。
 * 仅当解析到有效 `solutionRovider` 时返回非空结果。
 */
export function getCurrentEquipmentData(): CurrentEquipmentData {
  if (typeof window === "undefined") return null;
  try {
    const parsed = readMouseEquipmentDataFromStorage();
    if (!parsed) return null;
    const solutionRovider = parsed?.solutionRovider;
    if (typeof solutionRovider !== "string" || !solutionRovider.trim()) {
      return null;
    }
    return {
      displayname: parsed?.displayname as string | undefined,
      productId: Number(parsed?.productId ?? 0) || 0,
      productName: parsed?.productName as string | undefined,
      solutionRovider: solutionRovider.trim(),
      vendorId: Number(parsed?.vendorId ?? 0) || 0,
    };
  } catch {
    return null;
  }
}

type MouseZustandApi = {
  getState: () => {
    deviceInfo?: Record<string, unknown>;
    setDeviceInfo: (info: unknown) => void;
  };
};

function collectCandidateWindows(): Window[] {
  const out: Window[] = [];
  const seen = new Set<Window>();
  const push = (w: Window | null | undefined) => {
    if (!w || seen.has(w)) return;
    seen.add(w);
    out.push(w);
  };
  if (typeof window === "undefined") return out;
  push(window);
  try {
    push(window.parent);
  } catch {
    /* ignore */
  }
  try {
    push(window.top);
  } catch {
    /* ignore */
  }
  try {
    push(window.opener as Window | null);
  } catch {
    /* ignore */
  }
  try {
    for (let i = 0; i < window.length; i++) {
      try {
        push(window[i] as Window);
      } catch {
        /* cross-origin */
      }
    }
  } catch {
    /* ignore */
  }
  try {
    document.querySelectorAll("iframe").forEach((node) => {
      try {
        push((node as HTMLIFrameElement).contentWindow);
      } catch {
        /* ignore */
      }
    });
  } catch {
    /* ignore */
  }
  try {
    const box = document.getElementById("subapp-container");
    box?.querySelectorAll("iframe").forEach((node) => {
      try {
        push((node as HTMLIFrameElement).contentWindow);
      } catch {
        /* ignore */
      }
    });
  } catch {
    /* ignore */
  }
  return out;
}

function getSyncTargets(): Window[] {
  const targets = new Set<Window>(collectCandidateWindows());
  if (typeof window !== "undefined") {
    try {
      if (window.top) targets.add(window.top);
    } catch {
      /* ignore */
    }
  }
  return Array.from(targets);
}

function writeMouseInfoCache(detail: unknown, bumpSeq = true): void {
  try {
    (
      globalThis as unknown as { __MOUSE_DEVICE_INFO_LAST__?: unknown }
    ).__MOUSE_DEVICE_INFO_LAST__ = detail;
    if (!bumpSeq) return;
    (
      globalThis as unknown as { __MOUSE_DEVICE_INFO_SEQ__?: number }
    ).__MOUSE_DEVICE_INFO_SEQ__ =
      ((globalThis as unknown as { __MOUSE_DEVICE_INFO_SEQ__?: number })
        .__MOUSE_DEVICE_INFO_SEQ__ || 0) + 1;
  } catch {
    /* ignore */
  }
}

/** 切鼠/换方案时清掉上一台推送缓存，避免子应用侧栏电量/连接态串台 */
export function clearRememberedMouseDeviceInfo(): void {
  try {
    const g = globalThis as unknown as {
      __MOUSE_DEVICE_INFO_LAST__?: unknown;
      __MOUSE_DEVICE_INFO_SEQ__?: number;
    };
    g.__MOUSE_DEVICE_INFO_LAST__ = undefined;
    g.__MOUSE_DEVICE_INFO_SEQ__ =
      (typeof g.__MOUSE_DEVICE_INFO_SEQ__ === "number"
        ? g.__MOUSE_DEVICE_INFO_SEQ__
        : 0) + 1;
  } catch {
    /* ignore */
  }
}

/** getState 成功后记下整包，不 bump SEQ（否则 in-flight 的 getState 会被当成过期丢掉） */
export function rememberMouseDeviceInfo(detail: unknown): void {
  if (!detail || typeof detail !== "object") return;
  const rec = stripMouseDeviceInfoLiveRoutes({
    ...(detail as RecordLike),
  });
  if (!rec.mouseCfg || typeof rec.mouseCfg !== "object") return;
  // 刚改过键：HID 可能仍返回旧键，勿用这次读回盖掉 mergeKeys 写入的 LAST
  if (shouldKeepLastKeysAfterWrite()) {
    const lastKeys = (readLastMouseDeviceInfo()?.mouseCfg as { keys?: unknown })
      ?.keys;
    if (Array.isArray(lastKeys) && lastKeys.length > 0) {
      rec.mouseCfg = {
        ...(rec.mouseCfg as RecordLike),
        keys: lastKeys,
      };
    }
  }
  writeMouseInfoCache(rec, false);
}

function dispatchMouseInfoToWindow(target: Window, detail: unknown): void {
  try {
    target.dispatchEvent(new CustomEvent("mouseDeviceInfoPushed", { detail }));
    target.dispatchEvent(new CustomEvent("updateMouseUI", { detail }));
  } catch {
    /* ignore */
  }
}

function publishMouseDeviceInfo(detail: Record<string, unknown>): void {
  writeMouseInfoCache(detail);
  for (const target of getSyncTargets()) {
    dispatchMouseInfoToWindow(target, detail);
  }
}

/** 板载 profile 切换通知（宿主 + iframe，由 Menu 统一弹 Toast） */
export function dispatchMouseBoardProfileChanged(
  profile: number,
  opts?: {
    name?: string;
    source?: "user" | "device" | "sync" | "auto";
  }
): void {
  if (typeof window === "undefined") return;
  if (!Number.isFinite(profile)) return;
  const detail = {
    profile,
    name: opts?.name,
    source: opts?.source ?? ("user" as const),
  };
  for (const target of getSyncTargets()) {
    dispatchMouseBoardProfileChangedOnWindow(target, detail);
  }
}

/**
 * 对齐 freq-demo `MainProvider`：`statu` 回调里立刻 `setMouseDpi` / `setMouseReportRate`，不等待 getMouseConfigInfo。
 * 在宿主与各 iframe 的 mouseStoreApi 上合并 currentDpi、reportRate(Hz)。
 * 仅当当前 store 已是富芮坤快照时才写入；多鼠隔离由 registerFreqStatuListener 的 stableId 负责。
 */
export function applyFreqDemoDpiStatuNotify(value: {
  dpi: number;
  reportRate: number;
}): void {
  if (typeof window === "undefined") return;
  const hzTable = [125, 250, 500, 1000];
  const dpiIdx = Math.max(0, Math.floor(Number(value.dpi)) || 0);
  const rrIdx = Math.max(
    0,
    Math.min(3, Math.floor(Number(value.reportRate)) || 0)
  );
  const hz = hzTable[rrIdx] ?? 1000;

  let next: Record<string, unknown> | null = null;
  for (const w of getSyncTargets()) {
    try {
      const api = (w as unknown as { mouseStoreApi?: MouseZustandApi })
        .mouseStoreApi;
      const st = api?.getState?.();
      if (!st?.setDeviceInfo) continue;
      const di = st.deviceInfo;
      if (!di || typeof di !== "object") continue;
      const mc = (di.mouseCfg as Record<string, unknown>) || {};
      // 非富芮坤会话快照：不写，防止匠盟/贝盈等当前页被串改
      const isFreq = mc.freqLinkScheme === "a" || mc.freqLinkScheme === "c";
      if (!isFreq) continue;
      const storedMax = Math.max(1, Number(mc.maxDpiStage) || 6);
      // 富芮坤默认至少 6 档；g2sea 等固定档机型用固定档位数（避免抬到 6）
      const max = hasFixedFreqDpiGearCount()
        ? getFreqDpiGearCount()
        : Math.max(6, storedMax);
      const cur = Math.min(max - 1, dpiIdx);
      next = {
        ...di,
        mouseCfg: {
          ...mc,
          currentDpi: cur,
          reportRate: hz,
        },
      };
      st.setDeviceInfo(next);
    } catch {
      /* ignore */
    }
  }

  if (next) publishMouseDeviceInfo(next);
}

/** 深拷贝 trigger / 磁微动校准等高频变更字段，确保子应用能检测到嵌套更新 */
function cloneMouseCfgForUiSync(
  mouseCfg: Record<string, unknown> | undefined
): Record<string, unknown> | undefined {
  if (!mouseCfg || typeof mouseCfg !== "object") return mouseCfg;
  const next: Record<string, unknown> = { ...mouseCfg };
  const trigger = mouseCfg.trigger;
  if (trigger && typeof trigger === "object" && !Array.isArray(trigger)) {
    const t = trigger as Record<string, unknown>;
    next.trigger = {
      left:
        t.left && typeof t.left === "object"
          ? { ...(t.left as Record<string, unknown>) }
          : t.left,
      right:
        t.right && typeof t.right === "object"
          ? { ...(t.right as Record<string, unknown>) }
          : t.right,
    };
  }
  const cal = mouseCfg.magneticMicroswitchCal;
  if (cal && typeof cal === "object" && !Array.isArray(cal)) {
    const c = cal as Record<string, unknown>;
    next.magneticMicroswitchCal = {
      ...c,
      left:
        c.left && typeof c.left === "object"
          ? { ...(c.left as Record<string, unknown>) }
          : c.left,
      right:
        c.right && typeof c.right === "object"
          ? { ...(c.right as Record<string, unknown>) }
          : c.right,
    };
  }
  const dpis = mouseCfg.dpis;
  if (Array.isArray(dpis)) {
    next.dpis = dpis.map((row) => {
      if (!row || typeof row !== "object") return row;
      const d = row as Record<string, unknown>;
      return {
        ...d,
        ...(d.color != null ? { color: normalizeDpiColor(d.color) } : {}),
      };
    });
  }
  // 改键页依赖 keys 引用变化；切板载后必须换新数组才能触发 initializeKeys
  const keys = mouseCfg.keys;
  if (Array.isArray(keys)) {
    next.keys = keys.map((row) => {
      if (!row || typeof row !== "object" || Array.isArray(row)) {
        return Array.isArray(row) ? [...row] : row;
      }
      const k = row as Record<string, unknown>;
      const value = k.value;
      return {
        ...k,
        value: Array.isArray(value) ? [...value] : value,
      };
    });
  }
  // 灯光回显依赖嵌套对象引用变化（匠盟/展立灯光页都订阅这两块）
  const dpiEffect = mouseCfg.dpiEffect;
  if (dpiEffect && typeof dpiEffect === "object" && !Array.isArray(dpiEffect)) {
    next.dpiEffect = { ...(dpiEffect as Record<string, unknown>) };
  }
  const dongleRGBBar = mouseCfg.dongleRGBBar;
  if (
    dongleRGBBar &&
    typeof dongleRGBBar === "object" &&
    !Array.isArray(dongleRGBBar)
  ) {
    next.dongleRGBBar = { ...(dongleRGBBar as Record<string, unknown>) };
  }
  const sensor = mouseCfg.sensor;
  if (sensor && typeof sensor === "object" && !Array.isArray(sensor)) {
    next.sensor = { ...(sensor as Record<string, unknown>) };
  }
  return next;
}

/** 对齐 demo `{ ...mouseFuncInfo, ...patch }`：嵌套 sensor 也展开，避免整包盖掉虚拟漂移等字段 */
function overlayMouseCfg(
  base: Record<string, unknown>,
  extra: Record<string, unknown> | undefined
): Record<string, unknown> {
  if (!extra || typeof extra !== "object") return base;
  const next: Record<string, unknown> = { ...base, ...extra };
  const prevSensor =
    base.sensor &&
    typeof base.sensor === "object" &&
    !Array.isArray(base.sensor)
      ? (base.sensor as Record<string, unknown>)
      : undefined;
  const extraSensor =
    extra.sensor &&
    typeof extra.sensor === "object" &&
    !Array.isArray(extra.sensor)
      ? (extra.sensor as Record<string, unknown>)
      : undefined;
  if (prevSensor || extraSensor) {
    next.sensor = { ...(prevSensor ?? {}), ...(extraSensor ?? {}) };
  }
  const prevDpis = Array.isArray(base.dpis) ? base.dpis : undefined;
  const extraDpis = Array.isArray(extra.dpis) ? extra.dpis : undefined;
  if (prevDpis && extraDpis) {
    next.dpis = extraDpis.map((row, i) => {
      const prev = prevDpis[i];
      if (!row || typeof row !== "object" || Array.isArray(row)) return row;
      const extraRow = row as Record<string, unknown>;
      const prevRow =
        prev && typeof prev === "object" && !Array.isArray(prev)
          ? (prev as Record<string, unknown>)
          : undefined;
      return {
        ...(prevRow ?? {}),
        ...extraRow,
        color: extraRow.color ?? prevRow?.color,
      };
    });
  }
  return next;
}

function readLastMouseDeviceInfo(): Record<string, unknown> | null {
  try {
    const last = (
      globalThis as unknown as {
        __MOUSE_DEVICE_INFO_LAST__?: Record<string, unknown>;
      }
    ).__MOUSE_DEVICE_INFO_LAST__;
    if (last && typeof last === "object") return last;
  } catch {
    /* ignore */
  }
  return null;
}

/** mergeKeys 用：窗口 store 读不到时退回 LAST，避免改键整包推送 no-op */
export function readMouseDeviceInfoForMerge(): Record<string, unknown> | null {
  return readMouseDeviceInfoFromStore() || readLastMouseDeviceInfo();
}

/** 从宿主/子应用 mouseStoreApi 读取当前 deviceInfo（mergeDpi 等中间层用） */
export function readMouseDeviceInfoFromStore(): Record<string, unknown> | null {
  if (typeof window === "undefined") return null;
  let best: Record<string, unknown> | null = null;
  let bestScore = -1;
  for (const w of getSyncTargets()) {
    try {
      const current = (
        w as unknown as {
          mouseStoreApi?: { getState?: () => { deviceInfo?: unknown } };
        }
      ).mouseStoreApi?.getState?.()?.deviceInfo;
      if (!current || typeof current !== "object") continue;
      const di = current as Record<string, unknown>;
      const mc = (di.mouseCfg as Record<string, unknown>) || {};
      let score = Object.keys(mc).length;
      if (mc.sleepTime !== undefined) score += 20;
      if (mc.moveWakeup !== undefined) score += 20;
      if (mc.debounceTime !== undefined) score += 15;
      if (mc.sensor && typeof mc.sensor === "object") {
        score += 10;
        const sensor = mc.sensor as Record<string, unknown>;
        // 有 lod 的完整 sensor 优先，避免空 {} sensor 抢走「最佳快照」
        if (sensor.lod !== undefined) score += 25;
        if (sensor.virtualOffsetEnable !== undefined) score += 15;
      }
      if (score > bestScore) {
        bestScore = score;
        best = di;
      }
    } catch {
      /* ignore */
    }
  }
  return best;
}

/**
 * 对齐 demo `setMouseFuncInfo({ ...mouseFuncInfo, lod_value })`：
 * 先把 LAST + 各窗 mouseCfg/sensor 做字段并集，再叠本次 patch。
 * 不要用「字段最多」的旧快照整包当 base，否则改 LOD 会把虚拟漂移/按键响应冲掉。
 */
export function patchMouseDeviceInfo(options: {
  mouseCfg?: Record<string, unknown>;
  sensor?: Record<string, unknown>;
}): void {
  if (typeof window === "undefined") return;

  const applyPatch = (
    prevCfg: Record<string, unknown>
  ): Record<string, unknown> => {
    const nextCfg = overlayMouseCfg(prevCfg, options.mouseCfg);
    if (options.sensor) {
      const prevSensor =
        (nextCfg.sensor as Record<string, unknown> | undefined) || {};
      nextCfg.sensor = { ...prevSensor, ...options.sensor };
    }
    return nextCfg;
  };

  const scoreCfg = (mc: Record<string, unknown>): number => {
    let score = Object.keys(mc).length;
    if (mc.sleepTime !== undefined) score += 20;
    if (mc.moveWakeup !== undefined) score += 20;
    if (mc.debounceTime !== undefined) score += 15;
    const sensor = mc.sensor;
    if (sensor && typeof sensor === "object") {
      score += 10;
      const s = sensor as Record<string, unknown>;
      if (s.lod !== undefined) score += 25;
      if (s.virtualOffsetEnable !== undefined) score += 15;
    }
    return score;
  };

  let bestDi: Record<string, unknown> | null = null;
  let bestScore = -1;
  let mergedCfg: Record<string, unknown> = {};

  // getState 整包（demo 的 mouseFuncInfo）：qiankun 沙箱下各窗 store 可能读不到
  try {
    const cached = mouseGetStateCache.lastOk?.data;
    if (cached && typeof cached === "object") {
      const di = cached as Record<string, unknown>;
      const mc = (di.mouseCfg as Record<string, unknown>) || {};
      mergedCfg = overlayMouseCfg(mergedCfg, mc);
      const score = scoreCfg(mc);
      if (score > bestScore) {
        bestScore = score;
        bestDi = di;
      }
    }
  } catch {
    /* ignore */
  }

  const last = readLastMouseDeviceInfo();
  if (last) {
    const mc = (last.mouseCfg as Record<string, unknown>) || {};
    mergedCfg = overlayMouseCfg(mergedCfg, mc);
    const score = scoreCfg(mc);
    if (score > bestScore) {
      bestScore = score;
      bestDi = last;
    }
  }

  for (const w of getSyncTargets()) {
    try {
      const current = (
        w as unknown as {
          mouseStoreApi?: { getState?: () => { deviceInfo?: unknown } };
        }
      ).mouseStoreApi?.getState?.()?.deviceInfo;
      if (!current || typeof current !== "object") continue;
      const di = current as Record<string, unknown>;
      const mc = (di.mouseCfg as Record<string, unknown>) || {};
      mergedCfg = overlayMouseCfg(mergedCfg, mc);
      const score = scoreCfg(mc);
      if (score > bestScore) {
        bestScore = score;
        bestDi = di;
      }
    } catch {
      /* ignore */
    }
  }

  if (bestDi) {
    syncMouseDeviceInfoToSubApp({
      ...bestDi,
      mouseCfg: applyPatch(mergedCfg),
    });
    return;
  }

  // 尚无任何快照时：各窗就地合并（能写则写）
  for (const w of getSyncTargets()) {
    try {
      const api = (w as unknown as { mouseStoreApi?: MouseZustandApi })
        .mouseStoreApi;
      const st = api?.getState?.();
      if (!st?.setDeviceInfo) continue;
      const prev = st.deviceInfo;
      if (!prev || typeof prev !== "object") continue;
      const prevRec = prev as Record<string, unknown>;
      const prevCfg = (prevRec.mouseCfg as Record<string, unknown>) || {};
      const nextCfg = applyPatch(prevCfg);
      st.setDeviceInfo({
        ...prevRec,
        mouseCfg: cloneMouseCfgForUiSync(nextCfg) ?? nextCfg,
      });
    } catch {
      /* ignore */
    }
  }
}

/**
 * 仅合并各窗口 deviceInfo 的顶层字段（online/sleeping/battery…），
 * 保留该窗口自己的 mouseCfg，避免跨窗口「最佳快照」整包盖掉回报率/模式/灯效。
 * 不 bump SEQ、不广播 mouseDeviceInfoPushed，以免 NeedSync 短路复用陈旧整包。
 */
export function patchMouseDeviceInfoFields(
  fields: Record<string, unknown>
): void {
  if (typeof window === "undefined") return;
  for (const w of getSyncTargets()) {
    try {
      const api = (w as unknown as { mouseStoreApi?: MouseZustandApi })
        .mouseStoreApi;
      const st = api?.getState?.();
      if (!st?.setDeviceInfo) continue;
      const prev = st.deviceInfo;
      if (!prev || typeof prev !== "object") continue;
      const prevRec = prev as Record<string, unknown>;
      st.setDeviceInfo({
        ...prevRec,
        ...fields,
        mouseCfg: prevRec.mouseCfg,
      });
    } catch {
      /* ignore */
    }
  }
  try {
    const g = globalThis as unknown as {
      __MOUSE_DEVICE_INFO_LAST__?: Record<string, unknown>;
    };
    const last = g.__MOUSE_DEVICE_INFO_LAST__;
    if (last && typeof last === "object") {
      g.__MOUSE_DEVICE_INFO_LAST__ = {
        ...last,
        ...fields,
        mouseCfg: last.mouseCfg,
      };
    }
  } catch {
    /* ignore */
  }
}

/**
 * 将鼠标 deviceInfo 同步给 qiankun 子应用（宿主 + iframe + frames 上的 mouseStoreApi）
 */
export const syncMouseDeviceInfoToSubApp = (deviceInfo: unknown) => {
  if (typeof window === "undefined") return;

  const raw = deviceInfo as Record<string, unknown>;
  const prevFromStore = readMouseDeviceInfoFromStore() as
    | RecordLike
    | undefined;
  const mergedRaw = preserveLiveRoutesOnFullDeviceSync(
    prevFromStore,
    raw as RecordLike
  );
  const next: Record<string, unknown> = {
    ...mergedRaw,
    mouseCfg: cloneMouseCfgForUiSync(
      mergedRaw.mouseCfg as Record<string, unknown> | undefined
    ),
  };
  if (typeof next.isWired !== "boolean") {
    const fromStore = prevFromStore?.isWired;
    const fromCache = (
      globalThis as unknown as {
        __MOUSE_DEVICE_INFO_LAST__?: { isWired?: unknown };
      }
    ).__MOUSE_DEVICE_INFO_LAST__?.isWired;
    if (typeof fromStore === "boolean") next.isWired = fromStore;
    else if (typeof fromCache === "boolean") next.isWired = fromCache;
  }
  for (const w of getSyncTargets()) {
    try {
      const api = (w as unknown as { mouseStoreApi?: MouseZustandApi })
        .mouseStoreApi;
      if (api?.getState?.().setDeviceInfo) {
        api.getState().setDeviceInfo(next);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn(
        "[Mouse][syncMouseDeviceInfoToSubApp] 写入 mouseStoreApi 失败",
        error
      );
    }
  }
  publishMouseDeviceInfo(next);
};

type MouseTriggerLiveRoutesPayload = {
  left: number;
  right: number;
  calibrating?: boolean;
  calLeft?: number;
  calRight?: number;
};

const MOUSE_TRIGGER_LIVE_DEPTH_EVENT = "mouseTriggerLiveDepthChanged";

function patchMouseCfgLiveRoutes(
  mouseCfg: RecordLike | undefined,
  payload: MouseTriggerLiveRoutesPayload
): RecordLike | undefined {
  if (!mouseCfg || typeof mouseCfg !== "object") return mouseCfg;
  const next: RecordLike = { ...mouseCfg };
  const trigger = mouseCfg.trigger;
  const t =
    trigger && typeof trigger === "object" && !Array.isArray(trigger)
      ? (trigger as RecordLike)
      : {};
  const prevLeft =
    t.left && typeof t.left === "object" ? (t.left as RecordLike) : {};
  const prevRight =
    t.right && typeof t.right === "object" ? (t.right as RecordLike) : {};
  next.trigger = {
    ...t,
    left: { ...prevLeft, currentRoute: payload.left },
    right: { ...prevRight, currentRoute: payload.right },
  };
  if (payload.calibrating) {
    const cal = mouseCfg.magneticMicroswitchCal;
    if (cal && typeof cal === "object" && !Array.isArray(cal)) {
      const c = cal as RecordLike;
      const calLeft =
        c.left && typeof c.left === "object" ? (c.left as RecordLike) : {};
      const calRight =
        c.right && typeof c.right === "object" ? (c.right as RecordLike) : {};
      next.magneticMicroswitchCal = {
        ...c,
        left: {
          ...calLeft,
          currentRoute:
            payload.calLeft ?? payload.left ?? calLeft.currentRoute ?? 0,
        },
        right: {
          ...calRight,
          currentRoute:
            payload.calRight ?? payload.right ?? calRight.currentRoute ?? 0,
        },
      };
    }
  }
  return next;
}

function readSideRoute(side: unknown): number | undefined {
  if (!side || typeof side !== "object" || Array.isArray(side))
    return undefined;
  const v = (side as RecordLike).currentRoute;
  return typeof v === "number" && Number.isFinite(v) ? v : undefined;
}

/** 整包 sync：去掉 depth；校准进行中时从 store 恢复 cal.currentRoute */
function preserveLiveRoutesOnFullDeviceSync(
  prev: RecordLike | undefined,
  incoming: RecordLike
): RecordLike {
  const stripped = stripMouseDeviceInfoLiveRoutes(incoming);
  const prevCfg = (prev?.mouseCfg as RecordLike | undefined) ?? {};
  const prevCal = prevCfg.magneticMicroswitchCal as RecordLike | undefined;
  if (!prevCal?.state) return stripped;

  const calLeft = readSideRoute(prevCal.left);
  const calRight = readSideRoute(prevCal.right);
  const left = readSideRoute((prevCfg.trigger as RecordLike | undefined)?.left);
  const right = readSideRoute(
    (prevCfg.trigger as RecordLike | undefined)?.right
  );

  const payload: MouseTriggerLiveRoutesPayload = {
    left: left ?? 0,
    right: right ?? 0,
    calibrating: true,
    calLeft: calLeft ?? left ?? 0,
    calRight: calRight ?? right ?? 0,
  };

  const mc = (stripped.mouseCfg as RecordLike | undefined) ?? {};
  const patched = patchMouseCfgLiveRoutes(mc, payload);
  return patched ? { ...stripped, mouseCfg: patched } : stripped;
}

/** StatusChanged 实时按压深度：事件 + store patch（校准页读 store） */
export function syncMouseTriggerLiveRoutesToSubApp(
  payload: MouseTriggerLiveRoutesPayload
): void {
  if (typeof window === "undefined") return;

  const detail = { left: payload.left, right: payload.right };

  for (const w of getSyncTargets()) {
    try {
      w.dispatchEvent(
        new CustomEvent(MOUSE_TRIGGER_LIVE_DEPTH_EVENT, { detail })
      );
    } catch {
      /* ignore */
    }
    try {
      const api = (w as unknown as { mouseStoreApi?: MouseZustandApi })
        .mouseStoreApi;
      const st = api?.getState?.();
      if (!st?.setDeviceInfo) continue;
      const prev = st.deviceInfo;
      if (!prev || typeof prev !== "object") continue;
      const prevRec = prev as RecordLike;
      const prevCfg = (prevRec.mouseCfg as RecordLike | undefined) ?? {};
      const patchedCfg = patchMouseCfgLiveRoutes(prevCfg, payload);
      if (!patchedCfg) continue;
      st.setDeviceInfo({
        ...prevRec,
        mouseCfg: cloneMouseCfgForUiSync(patchedCfg) ?? patchedCfg,
      });
    } catch {
      /* ignore */
    }
  }
}

// ===================== 协议常量与 UI 选项（原 mouse/types.ts） =====================

/** 鼠标按键功能类型（与协议一致） */
export const MouseKeyFunction = {
  Disable: 0x00,
  MouseKey: 0x01,
  LeftKey: 0x0100,
  DPISwitch: 0x02,
  LeftRightRoll: 0x03,
  FireKey: 0x04,
  ShortcutKey: 0x05,
  Macro: 0x06,
  ReportRateSwitch: 0x07,
  LightSwitch: 0x08,
  ProfileSwitch: 0x09,
  DPILock: 0x0a,
  UpDownRoll: 0x0b,
} as const;

/** 配对状态 */
export const DevicePairResult = {
  Pairing: 0x01,
  Fail: 0x02,
  Success: 0x03,
} as const;

/** 连接状态 */
export const DeviceConnectState = {
  Disconnected: 0x00,
  Connecting: 0x01,
  Connected: 0x02,
  TimeOut: 0x03,
} as const;

export interface ReportRateOption {
  value: number;
  rate: number;
  option: string;
}

export const REPORT_RATES: ReportRateOption[] = [
  { value: 8, rate: 125, option: "125Hz" },
  { value: 4, rate: 250, option: "250Hz" },
  { value: 2, rate: 500, option: "500Hz" },
  { value: 1, rate: 1000, option: "1000Hz" },
  { value: 16, rate: 2000, option: "2000Hz" },
  { value: 32, rate: 4000, option: "4000Hz" },
  { value: 64, rate: 8000, option: "8000Hz" },
];

export interface SensorModeOption {
  value: number;
  option: string;
}

export const SENSOR_MODE_OPTIONS: SensorModeOption[] = [
  { value: 0, option: "LP" },
  { value: 1, option: "HP" },
  { value: 256, option: "Corded" },
];

export interface PerformanceOption {
  value: number;
  option: string;
}

export const PERFORMANCE_OPTIONS: PerformanceOption[] = [
  { value: 1, option: "10sec" },
  { value: 3, option: "30sec" },
  { value: 6, option: "1min" },
  { value: 12, option: "2min" },
  { value: 30, option: "5min" },
  { value: 60, option: "10min" },
  { value: 90, option: "15min" },
];
